/**
 * Determines whether a dismissal event (pointerDownOutside, focusOutside, or interactOutside)
 * on a Dialog should be prevented to avoid accidental closing.
 *
 * Scenarios protected:
 * 1. Clicks on blank spaces or elements INSIDE the Dialog content.
 * 2. Clicks on portalled elements (e.g. Radix Select, Popover, Calendar, DropdownMenu).
 * 3. Clicks when a portalled Select or Popover is currently open (so the click only dismisses the dropdown).
 */
export function shouldPreventDialogDismissal(
  event?: CustomEvent<{ originalEvent?: Event }> | Event,
  contentElement?: HTMLElement | null
): boolean {
  if (typeof document === "undefined") return false

  const customEvent = event as CustomEvent<{ originalEvent?: Event }> | undefined
  const originalEvent = customEvent?.detail?.originalEvent || (event instanceof Event ? event : undefined)
  const target = (originalEvent?.target as Node | null) || (customEvent?.target as Node | null)

  // 1. If click target is inside the Dialog content element
  if (contentElement && target && contentElement.contains(target)) {
    return true
  }

  // 2. Check composedPath if available (useful when DOM element re-rendered during event dispatch)
  if (originalEvent && typeof originalEvent.composedPath === "function") {
    const path = originalEvent.composedPath()
    const isInsideDialogPath = path.some((el) => {
      if (el instanceof HTMLElement) {
        return (
          el.getAttribute("data-slot") === "dialog-content" ||
          el.classList.contains("cartera-dialog") ||
          (contentElement && el === contentElement)
        )
      }
      return false
    })
    if (isInsideDialogPath) {
      return true
    }

    const isInsidePortalledPath = path.some((el) => {
      if (el instanceof HTMLElement) {
        return (
          el.getAttribute("data-slot") === "select-content" ||
          el.getAttribute("data-slot") === "popover-content" ||
          el.getAttribute("data-radix-popper-content-wrapper") !== null ||
          el.getAttribute("role") === "listbox" ||
          el.getAttribute("role") === "menu"
        )
      }
      return false
    })
    if (isInsidePortalledPath) {
      return true
    }
  }

  if (target instanceof Element) {
    // Target is inside the dialog content slot
    if (target.closest('[data-slot="dialog-content"], .cartera-dialog')) {
      return true
    }

    // 3. Target is inside a portalled layer (Select, Popover, Calendar, Radix Popper)
    if (
      target.closest(
        '[data-slot="select-content"], [data-slot="popover-content"], [data-radix-popper-content-wrapper], [role="listbox"], [role="menu"], [data-radix-focus-guard]'
      )
    ) {
      return true
    }
  }

  // 4. A portalled Select or Popover is currently open
  const hasOpenPortalledMenu = Boolean(
    document.querySelector(
      '[data-slot="select-content"][data-state="open"], [data-slot="popover-content"][data-state="open"], [data-radix-popper-content-wrapper][data-state="open"]'
    )
  )

  return hasOpenPortalledMenu
}

/**
 * Backward-compatible helper for existing usages and tests.
 */
export function shouldKeepDialogOpenForSelect(): boolean {
  return shouldPreventDialogDismissal()
}
